// Loads the map renderer exactly once per page.
//
// The renderer is MapLibre GL JS drawing OpenFreeMap vector tiles: no key, no
// account, and commercial use allowed. This file is the only place that knows
// that; the picker sees a `MapKit` and nothing else, so a different renderer is
// a change here and in maplibre-kit.ts.
//
// EVERYTHING HERE CAN FAIL, AND FAILING IS AN ORDINARY OUTCOME. No WebGL, a
// blocked chunk, a network that never answers — each rejects, and the picker
// answers a rejection by showing typed address fields. The deadline matters as
// much as the errors: the picker shows a disabled "Loading map…" state until
// this settles, so without one a stalled request would leave it unusable.
import type { Map as MapLibreMap, Marker as MapLibreMarker } from 'maplibre-gl';
import { fetchGeoConfig, GeoConfig } from '../../../../api/geo-client';

export type { MapLibreMap, MapLibreMarker };

export interface MapKit {
  Map: typeof import('maplibre-gl').Map;
  Marker: typeof import('maplibre-gl').Marker;
  NavigationControl: typeof import('maplibre-gl').NavigationControl;
  styleUrl: string;
  attribution: string;
}

const LOAD_TIMEOUT_MS = 10000;

/**
 * CAN THE PAGE AROUND AN ELEMENT SCROLL AT ALL?
 *
 * The map's "cooperative gestures" (two fingers to pan, Ctrl + wheel to zoom)
 * exist for one reason: a map inside a scrolling form must not swallow the
 * finger that is scrolling the form. Where NOTHING around the map can scroll —
 * a short form on a tall screen, most often an installed app on a tablet —
 * there is no scroll to protect, and the map's hint "use two fingers to move
 * the map" tells somebody to do something awkward for no reason (R-T11,
 * Plan C WP-C12). So the picker asks this and turns the gestures off there.
 *
 * True when any scrolling ancestor, or the document itself, has more content
 * than room. One pixel of slack: sub-pixel layout rounds both ways.
 */
export const pageCanScroll = (element: Element | null): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return true;
  let node = element ? element.parentElement : null;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) {
      return true;
    }
    node = node.parentElement;
  }
  const root = document.scrollingElement || document.documentElement;

  return root.scrollHeight > root.clientHeight + 1;
};

// MapLibre draws with WebGL2 and throws from its constructor without it.
// Checked up front so a browser that cannot draw the map never downloads it.
//
// The test context is given straight back. Safari keeps a small fixed number
// of live WebGL contexts per page and drops the OLDEST when a new one would
// exceed it — so a probe that was never released could cost the real map its
// context later.
const hasWebGl = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    if (!gl) return false;
    gl.getExtension('WEBGL_lose_context')?.loseContext();

    return true;
  } catch (error) {
    return false;
  }
};

let pending: Promise<MapKit> | null = null;

export const loadMapKit = (config: GeoConfig): Promise<MapKit> => {
  if (pending) return pending;

  const map = config.map;
  if (!map) return Promise.reject(new Error('The map is switched off.'));
  if (!hasWebGl()) {
    return Promise.reject(new Error('This browser cannot draw the map.'));
  }

  pending = new Promise<MapKit>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The map timed out.')),
      LOAD_TIMEOUT_MS
    );

    import('./maplibre-kit')
      .then(kit => {
        clearTimeout(timer);
        resolve({
          Map: kit.Map,
          Marker: kit.Marker,
          NavigationControl: kit.NavigationControl,
          styleUrl: map.style_url,
          attribution: map.attribution,
        });
      })
      .catch(error => {
        clearTimeout(timer);
        reject(error);
      });
  });

  // A failed load must not poison every later attempt (a flaky network).
  pending.catch(() => {
    pending = null;
  });

  return pending;
};

export { fetchGeoConfig };
